import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { ChipSelectSingle, ChipSelectMulti } from '@/shared/ui/components/form/ChipSelect';
import { CityAutocompleteInput } from '@/shared/ui/components/form/CityAutocompleteInput';
import { TagInput } from '@/shared/ui/components/form/TagInput';
import { getBusinessTypeOptions, type ClientFormData } from './schema';
import { PROPERTY_CATEGORIES, getCategoryLabel } from '@features/property-management/components/PropertyFormDualPane/schema';
import { maskCurrency } from '../../utils/masks';
import { FieldError } from './FieldError';
import {
  labelStyle,
  currencyInputPadding,
  currencyPrefixStyle,
} from './drawer-styles';
import { Label, Text, TextInput } from '@/shared/ui';
import {
  AmenityGroupFields,
  FurnishedAndPetFields,
  MinimumFields,
  PreferenceTagsField,
} from './PreferencesExtraFields';

interface PreferencesSectionProps {
  form: UseFormReturn<ClientFormData>;
  collapsed: boolean;
  onToggle: () => void;
}

const PROPERTY_TYPE_OPTIONS = PROPERTY_CATEGORIES.map((cat) => ({
  value: cat,
  label: getCategoryLabel(cat),
}));

/**
 * Optional demand profile fields for the client drawer (nested model).
 */
export const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  form,
  collapsed,
  onToggle,
}) => {
  const { t } = useTranslation('features/clients');
  return (
    <FormSection
      title={t('drawer.preferences.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <Controller
        name="demandProfile.propertyKinds"
        control={form.control}
        render={({ field }) => (
          <ChipSelectMulti
            label={t('drawer.preferences.propertyType')}
            options={PROPERTY_TYPE_OPTIONS}
            value={field.value || []}
            onChange={field.onChange}
            optionsTestId="preferences-property-chips"
            optionsClassName="max-h-[8rem] overflow-y-auto pr-(--space-1)"
          />
        )}
      />

      <Controller
        name="demandProfile.businessType"
        control={form.control}
        render={({ field }) => (
          <ChipSelectSingle
            label={t('drawer.preferences.businessType')}
            options={getBusinessTypeOptions(t)}
            value={field.value || ''}
            onChange={field.onChange}
          />
        )}
      />

      <div className="grid grid-cols-2 gap-(--space-3)">
        <Controller
          name="demandProfile.priceMin"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.preferences.priceMin')}</Label>
              <div className="relative">
                <Text component="span" style={currencyPrefixStyle}>R$</Text>
                <TextInput
                  {...field}
                  placeholder="0"
                  onChange={(e) => field.onChange(maskCurrency(e.target.value))}
                  style={currencyInputPadding}
                />
              </div>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
        <Controller
          name="demandProfile.priceMax"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col">
              <Label style={labelStyle}>{t('drawer.preferences.priceMax')}</Label>
              <div className="relative">
                <Text component="span" style={currencyPrefixStyle}>R$</Text>
                <TextInput
                  {...field}
                  placeholder="0"
                  onChange={(e) => field.onChange(maskCurrency(e.target.value))}
                  style={currencyInputPadding}
                />
              </div>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      <Controller
        name="demandProfile.cities"
        control={form.control}
        render={({ field }) => (
          <CityAutocompleteInput
            label={t('drawer.preferences.cities')}
            value={field.value || []}
            onChange={field.onChange}
            placeholder={t('drawer.preferences.citiesPlaceholder')}
          />
        )}
      />

      <Controller
        name="demandProfile.neighborhoods"
        control={form.control}
        render={({ field }) => (
          <TagInput
            label={t('drawer.preferences.neighborhoods')}
            value={field.value || []}
            onChange={field.onChange}
            placeholder={t('drawer.preferences.neighborhoodsPlaceholder')}
          />
        )}
      />

      <MinimumFields form={form} />
      <FurnishedAndPetFields form={form} />
      <AmenityGroupFields form={form} />
      <PreferenceTagsField form={form} />
    </FormSection>
  );
};
